/**
 * "Late is a date, not a status." Lists and records keep their status chip; a late row gets an
 * amber edge and its date reads "2 days late". The list and record RPCs compute `days_late` in
 * each site's own time zone; these helpers only word it.
 */

function wholeDays(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.trunc(value);
}

/** "1 day late", "12 days late"; null when not late. */
export function formatDaysLate(daysLate: number | null | undefined): string | null {
  const days = wholeDays(daysLate);
  if (days == null || days <= 0) return null;
  return days === 1 ? '1 day late' : `${days} days late`;
}

export function isLate(daysLate: number | null | undefined): boolean {
  const days = wholeDays(daysLate);
  return days != null && days > 0;
}

/** "today", "tomorrow", "in 3 days"; null for past dates (use formatDaysLate for those). */
export function formatDueIn(daysUntil: number | null | undefined): string | null {
  const days = wholeDays(daysUntil);
  if (days == null || days < 0) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/**
 * Whole calendar days from `from` to `to` (both `YYYY-MM-DD`), e.g. for a rental window.
 * Inclusive windows add one day themselves.
 */
export function calendarDaysBetween(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const start = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 86_400_000);
}
